import React, { useRef, useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import PropTypes from 'prop-types';
import { t } from 'i18next';
import type { ChartBaseProps } from "../types/chart-types";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * LivisAreaHighcharts - A customizable area chart component
 * 
 * Features:
 * - Supports single or multiple series data
 * - Configurable stacking modes
 * - Customizable line and marker styling
 * - Optional legend and tooltips
 * - Responsive design
 * - Transparent background for dashboard integration
 * - Empty state handling with custom message
 * 
 * @param props - Component configuration and data
 * @returns Rendered Highcharts area chart component
 */
interface LivisAreaHighchartsComponentProps extends ChartBaseProps {
  data: any[];
  title?: string;
  subtitle?: string;
  stacking?: 'normal' | 'percent' | 'none';
  lineWidth?: number;
  markerEnabled?: boolean;
  markerRadius?: number;
  fillOpacity?: number;
  showLegend?: boolean;
  tooltip?: boolean;
  tooltipFormatter?: Highcharts.TooltipFormatterCallbackFunction;
  onClickPoint?: (event: {
    x: number;
    y: number;
    series: { name: string; type: string };
    point: Highcharts.Point;
  }) => void;
  enablePagination?: boolean;
  pageSize?: number;
  showNavigationArrows?: boolean;
  legendPosition?: string;
  showXAxisLabels?: boolean;
  showYAxisLabels?: boolean;
  showXAxisLine?: boolean;
  showYAxisLine?: boolean;
  showXAxisGrid?: boolean;
  showYAxisGrid?: boolean;
  showAllGrid?: boolean;
  noDataMessage?: string; // NEW: Custom no data message
  x_title?: string;
  y_title?: string;
  categories?: string[];  
  yAxisConfig?: {
    max?: number;
    min?: number;
    tickInterval?: number;
    labels?: {
      format?: string;
    };
  };
}

const LivisAreaHighcharts: React.FC<LivisAreaHighchartsComponentProps> = ({
  data,
  height = 400,
  title,
  subtitle,
  stacking = 'none',
  lineWidth = 1,
  markerEnabled = false,
  markerRadius = 5,
  fillOpacity = 0.2,
  showLegend = false,
  tooltip = true,
  tooltipFormatter,
  onClickPoint,
  enablePagination = false,
  pageSize = 8,
  showNavigationArrows = false,
  legendPosition = 'top',
  showXAxisLabels = true,
  showYAxisLabels = true,
  showXAxisLine = true,
  showYAxisLine = true,
  showXAxisGrid = false,
  showYAxisGrid = true,
  showAllGrid = true,
  noDataMessage = "No Record Found", // NEW: Default message,
  x_title,
  y_title,
  categories,
  yAxisConfig,
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const [currentPage, setCurrentPage] = useState(0);

  // NEW: Check for empty data and render no data message
  if (!data || data.length === 0) {
    return (
      <div 
        className="livis-dashboard-no-record-centered"
        style={{ 
          width: '100%', 
          height: typeof height === 'number' ? `${height}px` : height, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}
      >
        <p>{t(noDataMessage)}</p>
      </div>
    );
  }

  // Calculate pagination data
  const totalDataPoints = data.length > 0 && data[0]?.data ? data[0].data.length : 0;
  const totalPages = enablePagination ? Math.ceil(totalDataPoints / pageSize) : 1;
  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;

  // Get paginated data
  const getPaginatedData = () => {
    if (!enablePagination) return data;

    return data.map(series => ({
      ...series,
      data: series.data.slice(startIndex, endIndex)
    }));
  };

  // Get paginated categories
  const getPaginatedCategories = () => {
    // Prefer provided categories if available
    if (categories && categories.length > 0) {
      return enablePagination ? categories.slice(startIndex, endIndex) : categories;
    }

    // If series data points are objects with a category/x/name, derive categories from them
    const firstSeries = data && data.length > 0 ? data[0] : undefined;
    let derivedCategories: string[] = [];

    if (firstSeries && Array.isArray(firstSeries.data) && firstSeries.data.length > 0) {
      const derived = firstSeries.data.map((pt: any, idx: number) => {
        if (pt == null) return String(idx);
        if (typeof pt === 'object') {
          return pt.category ?? pt.x ?? pt.name ?? String(idx);
        }
        // primitive value - fallback label
        return String(pt);
      });
      derivedCategories = derived;
    } else {
      // Fallback synthetic categories to preserve previous behaviour
      derivedCategories = Array.from({ length: totalDataPoints }, (_, index) => `Sep ${10 + index}`);
    }

    return enablePagination ? derivedCategories.slice(startIndex, endIndex) : derivedCategories;
  };

  // Helper to normalize series for Highcharts: ensure series.data is array of numeric y values or { y, name }
  const getNormalizedSeries = () => {
    const paginated = getPaginatedData();

    return paginated.map((series: any) => {
      // If series.data contains objects, convert to numbers or { y, name }
      if (Array.isArray(series.data) && series.data.length > 0 && typeof series.data[0] === 'object') {
        const normalizedData = series.data.map((pt: any) => {
          if (pt == null) return 0;
          // support { y, category } or { value, category } or { name, value }
          const value = pt.y ?? pt.value ?? pt.total ?? pt.count ?? null;
          const numeric = typeof value === 'number' ? value : Number(value ?? 0);
          // Highcharts can accept { y, name }
          const name = pt.category ?? pt.x ?? pt.name ?? undefined;
          return name !== undefined ? { y: numeric, name } : numeric;
        });

        return {
          ...series,
          data: normalizedData
        };
      }

      // Already primitive array
      return series;
    });
  };

  // Navigation handlers
  const handlePrevious = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };

  // Configure global Highcharts settings
  Highcharts.setOptions({
    lang: {
      thousandsSep: ','
    }
  });

  const options: Highcharts.Options = {
    chart: {
      type: 'area',
      height: typeof height === 'number' ? `${height}px` : height,
      backgroundColor: 'transparent',
      spacingTop: showLegend && legendPosition === 'top-right' ? 60 : 20
    },

    // Chart titles
    title: { text: title ?? undefined },
    subtitle: { text: subtitle ?? undefined },

    // X-axis configuration with new props
    xAxis: {
      categories: getPaginatedCategories(),
      tickLength: showXAxisLine ? undefined : 0,
      lineWidth: showXAxisLine ? 1 : 0,
      lineColor: 'var(--livis-usecase-horizontal-line-color)',
      gridLineWidth: showAllGrid && showXAxisGrid ? 1 : 0,
      gridLineColor: 'var(--livis-usecase-horizontal-line-color)',
      labels: {
        enabled: showXAxisLabels,
        style: {
          color: 'var(--livis-gray-font-color)',
          fontSize: '0.75rem'
        }
      },
      title: x_title ? { text: x_title, style: { fontSize: '14px', fontWeight: '600' } } : { text: undefined },
    },

    // Y-axis configuration with new props
    yAxis: {
      title: y_title ? { text: y_title, style: { fontSize: '14px', fontWeight: '600' } } : { text: undefined },
      gridLineWidth: showAllGrid && showYAxisGrid ? 1 : 0,
      gridLineColor: 'var(--livis-usecase-horizontal-line-color)',
      lineWidth: showYAxisLine ? 1 : 0,
      tickLength: showYAxisLine ? undefined : 0,
      max: yAxisConfig?.max,
      
      labels: {
        enabled: showYAxisLabels,
        formatter: function () {
          const value = Number(this.value);
          // Format with thousands separator
          const formatted = value.toLocaleString(undefined);
          return stacking === 'percent' ? `${formatted}%` : `${formatted}`;
        },
        style: {
          color: 'var(--livis-gray-font-color)',
          fontSize: '0.75rem'
        }
      
      }
    },

    // Tooltip configuration
    tooltip: {
      enabled: tooltip,
      shared: true,
      backgroundColor: 'var(--livis-recipe-details-part-footer-color)',
      borderWidth: 0,
      borderRadius: 8,
      shadow: false,
      padding: 16,
      style: {
        color: '#fff'
      },
      formatter: function () {
        const xLabel = this.x ?? '';
        let tooltipText = `<b style="color: var(--livis-white-color)">${xLabel}</b><br/>`;
        this.points?.forEach(point => {
          // point.y might be a number; ensure numeric formatting
          const raw = point.y ?? 0;
          const value = typeof raw === 'number' ? raw : Number(raw);
          const formatted = value.toLocaleString(undefined);
          tooltipText += `<span style="color:${point.color}">${point.series.name}</span>: <b style="color: var(--livis-white-color)">${stacking === 'percent' ? formatted + '%' : formatted}</b><br/>`;
        });
        return tooltipText;
      }
    },

    // Plot options for chart behavior and styling
    plotOptions: {
      area: {
        stacking: stacking === 'none' ? undefined : stacking,
        lineWidth: lineWidth,
        fillOpacity: fillOpacity,
        marker: {
          enabled: markerEnabled,
          states: {
            hover: {
              enabled: true,
              radius: markerRadius + 2
            }
          }
        },
        states: {
          hover: {
            lineWidth: lineWidth + 1
          }
        },
        point: {
          events: {
            click: function (this: Highcharts.Point) {
              console.log('Point clicked:', this);
              if (onClickPoint) {
                onClickPoint({
                  x: this.x,
                  y: this.y ?? 0,
                  series: {
                    name: this.series.name,
                    type: this.series.type
                  },
                  point: this
                });
              }
            }
          }
        }
      }
    },

    // Series configuration
    // Use normalized series so each point provides numeric y values and optional names
    series: getNormalizedSeries().map((series: any) => ({
      ...series,
      cursor: 'pointer'
    })),

    // UI elements
    credits: { enabled: false },
    legend: {
      enabled: showLegend,
      align: legendPosition === 'top-right' ? 'right' : 'center',
      verticalAlign: 'top',
      floating: legendPosition === 'top-right',
      x: legendPosition === 'top-right' ? -10 : 0,
      y: legendPosition === 'top-right' ? 10 : 0,
      backgroundColor: legendPosition === 'top-right' ? '#F8F9FA' : 'transparent',
      borderWidth: legendPosition === 'top-right' ? 1 : 0,
      borderColor: '#E5E7EB',
      borderRadius: 8,
      padding: 16,
      itemStyle: {
        color: 'var(--livis-black-font-color)',
        fontSize: '0.75rem',
        fontWeight: '500'
      },
      symbolHeight: 12,
      symbolWidth: 16,
      symbolRadius: 0
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />

      {/* Navigation arrows */}
      {enablePagination && showNavigationArrows && (
        <>
          <div className="livis-reports-analytics-pagination-container">
            <button
              className={`livis-reports-analytics-pagination-btn${currentPage === 0 ? " disabled" : ""}`}
              onClick={handlePrevious}
              disabled={currentPage === 0}
              aria-label="Previous"
            >
              <ChevronLeft />
            </button>
            <button
              className={`livis-reports-analytics-pagination-btn${currentPage === totalPages - 1 ? " disabled" : ""}`}
              onClick={handleNext}
              disabled={currentPage === totalPages - 1}
              aria-label="Next"
            >
              <ChevronRight />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// PropTypes validation
LivisAreaHighcharts.propTypes = {
  data: PropTypes.array.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  stacking: PropTypes.oneOf(['normal', 'percent', 'none']),
  lineWidth: PropTypes.number,
  markerEnabled: PropTypes.bool,
  markerRadius: PropTypes.number,
  fillOpacity: PropTypes.number,
  showLegend: PropTypes.bool,
  tooltip: PropTypes.bool,
  tooltipFormatter: PropTypes.func,
  onClickPoint: PropTypes.func,
  enablePagination: PropTypes.bool,
  pageSize: PropTypes.number,
  showNavigationArrows: PropTypes.bool,
  legendPosition: PropTypes.string,
  showXAxisLabels: PropTypes.bool,
  showYAxisLabels: PropTypes.bool,
  showXAxisLine: PropTypes.bool,
  showYAxisLine: PropTypes.bool,
  showXAxisGrid: PropTypes.bool,
  showYAxisGrid: PropTypes.bool,
  showAllGrid: PropTypes.bool,
  noDataMessage: PropTypes.string, // NEW
  categories: PropTypes.array,
};

// Default props
LivisAreaHighcharts.defaultProps = {
  height: 400,
  stacking: 'none',
  lineWidth: 1,
  markerEnabled: false,
  markerRadius: 5,
  fillOpacity: 0.2,
  showLegend: false,
  tooltip: true,
  enablePagination: false,
  pageSize: 8,
  showNavigationArrows: false,
  legendPosition: 'top',
  showXAxisLabels: true,
  showYAxisLabels: true,
  showXAxisLine: true,
  showYAxisLine: true,
  showXAxisGrid: false,
  showYAxisGrid: true,
  showAllGrid: true,
  noDataMessage: "No Record Found" // NEW
};

export default LivisAreaHighcharts;